import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BusinessHoursEditor } from "@/components/pages/call-flows/components/BusinessHoursEditor";
import DaysOffCalendar from "@/components/team/DaysOffCalendar";
import WorkingHoursWeek from "@/components/team/WorkingHoursWeek";
import { BusinessHoursSchedule } from "@/model/interface/CallFlow";
import { UserPresence, WorkingHoursRequest } from "@/model/interface/Membership";
import { DEFAULT_WORKING_HOURS, hasWorkingHours } from "@/util/availability";
import { apiErrorMessage } from "@/util/apiError";

interface WorkingHoursDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  presence?: UserPresence | null;
  defaultTimeZone: string;
  followHours?: boolean;
  onSave: (request: WorkingHoursRequest) => Promise<unknown>;
}

const TIME_ZONES = [
  { value: "America/New_York", label: "Eastern Time (New York)" },
  { value: "America/Chicago", label: "Central Time (Chicago)" },
  { value: "America/Denver", label: "Mountain Time (Denver)" },
  { value: "America/Phoenix", label: "Arizona (Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)" },
  { value: "America/Anchorage", label: "Alaska (Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Honolulu)" },
];

export default function WorkingHoursDialog({
  open,
  onOpenChange,
  title,
  presence,
  defaultTimeZone,
  followHours,
  onSave,
}: WorkingHoursDialogProps) {
  const [schedule, setSchedule] = useState<BusinessHoursSchedule>(DEFAULT_WORKING_HOURS);
  const [timeZone, setTimeZone] = useState(defaultTimeZone);
  const [isFollowing, setIsFollowing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const canSave = !isFollowing || hasWorkingHours(schedule);
  const timeZoneOptions = TIME_ZONES.some((zone) => zone.value === timeZone)
    ? TIME_ZONES
    : [...TIME_ZONES, { value: timeZone, label: timeZone }];

  const save = async () => {
    setIsSaving(true);
    try {
      await onSave({
        workingHours: schedule,
        timezone: timeZone,
        availabilityMode: isFollowing ? "WORKING_HOURS" : "ALWAYS",
      });
      toast.success("Working hours saved");
      onOpenChange(false);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Could not save the working hours"));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const savedHours = presence?.workingHours;
    setSchedule(savedHours && hasWorkingHours(savedHours) ? savedHours : DEFAULT_WORKING_HOURS);
    setTimeZone(presence?.timezone ?? defaultTimeZone);
    setIsFollowing(followHours ?? presence?.availabilityMode === "WORKING_HOURS");
  }, [open, presence, defaultTimeZone, followHours]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Calls from flows and teams only ring inside these hours. A transfer someone picks still rings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="follow-working-hours">Follow these hours</Label>
              <p className="text-xs text-muted-foreground">When this is off, calls can ring at any time.</p>
            </div>
            <Switch id="follow-working-hours" checked={isFollowing} onCheckedChange={setIsFollowing} />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Label>Time zone</Label>
            <Select value={timeZone} onValueChange={setTimeZone}>
              <SelectTrigger className="w-[260px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeZoneOptions.map((zone) => (
                  <SelectItem key={zone.value} value={zone.value}>
                    {zone.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Week at a glance</p>
            <WorkingHoursWeek schedule={schedule} timeZone={timeZone} />
          </div>

          <BusinessHoursEditor
            value={schedule}
            title="Hours"
            description=""
            closedLabel="Unavailable"
            onChange={setSchedule}
          />

          <div className="space-y-2">
            <Label>Days off</Label>
            <DaysOffCalendar
              value={schedule.holidays ?? []}
              onChange={(holidays) => setSchedule({ ...schedule, holidays })}
            />
          </div>

          {!canSave && (
            <p className="text-xs text-destructive">Add hours on at least one day, or turn off Follow these hours.</p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave || isSaving}>
            Save hours
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
